/**
 * classicInvoiceTemplate — the boxed GST tax invoice most Indian shops already
 * recognise, as produced by the accounting packages they came from.
 *
 * ReceiptData -> standalone HTML for A4, print-background on. Same contract and
 * same constraints as a4InvoiceTemplate: inline CSS, no external assets, no
 * JavaScript, so the PDF window can disable the JS context entirely.
 *
 * ── How it differs from a4InvoiceTemplate ───────────────────────────────────
 * Not a restyle — a different information design, which is why both exist:
 *
 *   • One continuous ruled frame. Every block is a cell in one table, so the
 *     page reads as a single document rather than stacked panels. Buyers who
 *     file these by hand expect the ruled box.
 *   • The line table carries NO per-line tax. Rate, per and Amount only; all
 *     tax lands in the HSN summary at the foot. That is what the layout this
 *     was modelled on does, and Rule 46 is still satisfied because the
 *     rate-wise summary carries the rate and amount of tax for every HSN.
 *   • The item table is given a fixed minimum height so the ruled columns run
 *     down the page even for a six-line bill, which is what makes a printed
 *     form read as a form rather than a stub.
 *   • Totals sit inside the table body, right-aligned under the Amount column,
 *     rather than in a separate totals panel.
 *   • Carries the statutory copy marking, the seller's bank block, both
 *     amounts in words, and "E. & O.E".
 *
 * Reference fields (Delivery Note, Buyer's Order No., Dispatched through,
 * Destination and the rest) are ruled boxes beside Invoice No. They are filled
 * when the bill carries them — Quick Bill saves them as labelled lines in the
 * invoice's Terms (billing/invoiceExtras.ts) — and left blank for writing by
 * hand otherwise. Only the free-form part of Terms goes under Declaration.
 */

import type {
  InvoiceCopyType,
  ReceiptData,
  ReceiptTaxSummaryRow,
} from '../contracts/ReceiptContracts';
import { INVOICE_COPY_LABELS } from '../contracts/ReceiptContracts';
import { amountInWordsINR, escapeHtml, num } from './invoiceFormat';
import { parseTerms, type DispatchDetails } from '../../billing/invoiceExtras';
import {
  cellValue,
  columnWidths,
  defaultA4Columns,
  resolveA4Columns,
  type A4ColumnDef,
  type A4ColumnId,
} from './a4Columns';

export interface ClassicInvoiceOptions {
  /** Which statutory copy this is. Defaults to the original. */
  copyType?: InvoiceCopyType;
  /** When the document was printed, as distinct from the invoice date. */
  printedAt?: Date;
  /**
   * Which columns the item table prints. Defaults to the standard set; the
   * statutory four are re-added whatever is passed, so no caller can produce
   * an unlawful invoice by omission.
   */
  columns?: A4ColumnId[];
}

/** "12-Sep-26 at 11:42" — the print stamp, in the form the layout uses. */
const printStamp = (d: Date): string => {
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const dd = String(d.getDate()).padStart(2, '0');
  const mon = MONTHS[d.getMonth()];
  const yy = String(d.getFullYear()).slice(-2);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${dd}-${mon}-${yy} at ${hh}:${mm}`;
};

/** Quantity as "6 PCS" — whole where it is whole, so 6 does not print 6.000. */
const qtyWithUnit = (qty: number, unit?: string): string => {
  const n = Number.isInteger(qty)
    ? String(qty)
    : qty.toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
  return unit ? `${n} ${escapeHtml(unit.toUpperCase())}` : n;
};

/**
 * The money that belongs in the Amount column: the TAXABLE value, not the
 * tax-inclusive line total.
 *
 * This layout has a single Amount column and then adds CGST/SGST underneath it.
 * `ReceiptLineItem.amount` is the line total WITH tax in it (the adapter maps
 * it from lineItem.totalAmount), so using it here produced a document that
 * contradicted itself: the column summed to 4,106.40, the tax lines were
 * printed below, and the grand total was still 4,106.40 — tax shown but never
 * added. Read across a real bill and the arithmetic has to work:
 *
 *     2,100.00 + ... + 357.20 = 3,480.00  (taxable)
 *              + 313.20 + 313.20          (CGST + SGST)
 *              = 4,106.40                 (payable)
 *
 * a4InvoiceTemplate can use `amount` because it prints taxable and amount as
 * SEPARATE columns; this one cannot.
 */
const lineAmount = (it: { amount: number; taxableValue?: number }): number =>
  typeof it.taxableValue === 'number' ? it.taxableValue : it.amount;

export function buildClassicInvoiceHtml(
  data: ReceiptData,
  options: ClassicInvoiceOptions = {},
): string {
  const copyType: InvoiceCopyType = options.copyType ?? 'ORIGINAL';
  const printedAt = options.printedAt ?? new Date();
  const interState = data.isInterState === true;
  const summary: ReceiptTaxSummaryRow[] = data.taxSummary ?? [];
  const hasCess = summary.some((r) => r.cess > 0);

  // ── Seller ────────────────────────────────────────────────────────────────
  const sellerLines = [
    data.addressLine ? escapeHtml(data.addressLine) : '',
    data.gstin ? `GSTIN/UIN: ${escapeHtml(data.gstin)}` : '',
    data.stateName
      ? `State Name : ${escapeHtml(data.stateName)}${data.stateCode ? `, Code : ${escapeHtml(data.stateCode)}` : ''}`
      : '',
    data.phone ? `Contact : ${escapeHtml(data.phone)}` : '',
    data.email ? `E-Mail : ${escapeHtml(data.email)}` : '',
  ]
    .filter(Boolean)
    .map((l) => `<div>${l}</div>`)
    .join('');

  // ── Buyer ─────────────────────────────────────────────────────────────────
  const buyer = data.customer;
  const buyerName = buyer?.name || data.customerName || 'Walk-in customer';
  const buyerLines = buyer
    ? [
        buyer.address ? escapeHtml(buyer.address) : '',
        buyer.gstin ? `GSTIN/UIN : ${escapeHtml(buyer.gstin)}` : '',
        buyer.stateName
          ? `State Name : ${escapeHtml(buyer.stateName)}${buyer.stateCode ? `, Code : ${escapeHtml(buyer.stateCode)}` : ''}`
          : '',
        buyer.phone ? `Contact : ${escapeHtml(buyer.phone)}` : '',
      ]
        .filter(Boolean)
        .map((l) => `<div>${l}</div>`)
        .join('')
    : '';

  // ── Header extras ─────────────────────────────────────────────────────────
  //
  // The logo is embedded only when it is already a data: URI. An A4 template
  // must stay self-contained — the PDF window has no network, so an http(s)
  // src would print as a broken image.
  const logoBlock =
    data.logoUrl && data.logoUrl.startsWith('data:')
      ? `<img class="logo" src="${escapeHtml(data.logoUrl)}" alt=""/>`
      : '';

  // Place of supply is a Rule 46(n) particular on an INTER-state supply. On an
  // intra-state bill both state codes are already printed above, so the row is
  // omitted rather than padding the grid with something self-evident.
  const posRow =
    interState && data.placeOfSupply
      ? `<tr><td colspan="2"><div class="meta-k">Place of Supply</div><div class="meta-v">${escapeHtml(
          data.placeOfSupply,
        )}${data.placeOfSupplyCode ? `, Code : ${escapeHtml(data.placeOfSupplyCode)}` : ''}</div></td></tr>`
      : '';

  // ── Line items ────────────────────────────────────────────────────────────
  const cols = resolveA4Columns(options.columns ?? defaultA4Columns());
  const widths = columnWidths(cols);
  const colCount = cols.length;

  // The running totals under the item list — subtotal, each tax, round off —
  // are totals OF the Amount column, so they sit under it with their label in
  // the column to its left, wherever the merchant's selection has put it.
  const amountIdx = cols.findIndex((c) => c.id === 'amount');
  const labelIdx = Math.max(0, amountIdx - 1);

  const headerCells = cols
    .map((c, i) => `<th style="width:${widths[i].toFixed(2)}%">${c.label}</th>`)
    .join('');

  const cellClass = (c: A4ColumnDef) =>
    [c.align ?? '', c.bold ? 'b' : '', c.id === 'description' ? 'desc' : '']
      .filter(Boolean)
      .join(' ');

  const itemRows = data.items
    .map((it, i) => {
      const ctx = { index: i, taxable: lineAmount(it), num, qtyWithUnit };
      const cells = cols
        .map((c) => {
          // Everything is escaped here rather than per-column, so a product
          // name carrying < or & cannot reach the page as markup no matter
          // which columns are on.
          const cls = cellClass(c);
          return `<td${cls ? ` class="${cls}"` : ''}>${escapeHtml(cellValue(c.id, it, ctx))}</td>`;
        })
        .join('');
      return `
      <tr class="item">${cells}</tr>`;
    })
    .join('');

  const goodsTotal = data.items.reduce((a, it) => a + lineAmount(it), 0);

  /**
   * One running-total row — subtotal, a tax, round off.
   *
   * Every row carries a cell per column rather than a colspan. With a colspan
   * the internal column rules stop at the last item and the eye is left with
   * lines ending in mid-page, which is what a ruled form must never do. The
   * label sits in the column left of Amount and is allowed to spill further
   * left over the empty cells beside it.
   */
  const totalRow = (label: string, value: string, cls = ''): string => {
    const cells = Array.from({ length: colCount }, (_, i) => {
      if (i === amountIdx) return `<td class="r${cls ? ` ${cls}` : ''}">${value}</td>`;
      if (i === labelIdx && label) return `<td class="r italic taxlabel">${label}</td>`;
      return '<td></td>';
    }).join('');
    return `
      <tr${cls ? ` class="${cls}"` : ''}>${cells}</tr>`;
  };

  const totalLineRows: string[] = [];
  totalLineRows.push(totalRow('', num(goodsTotal), 'sub b'));
  for (const t of data.taxes) {
    totalLineRows.push(totalRow(escapeHtml(t.label), num(t.amount)));
  }
  if (typeof data.roundOff === 'number' && data.roundOff !== 0) {
    totalLineRows.push(totalRow('ROUND OFF', num(data.roundOff)));
  }

  // ── Total quantity, in the units actually sold ────────────────────────────
  // "66 PCS" only reads as a total when every line shares a unit. Mixed units
  // cannot be summed into one number, so they are listed per unit rather than
  // silently adding kilograms to pieces.
  const byUnit = new Map<string, number>();
  for (const it of data.items) {
    const u = (it.unit || '').toUpperCase();
    byUnit.set(u, (byUnit.get(u) ?? 0) + it.qty);
  }
  const totalQtyLabel = [...byUnit.entries()]
    .map(([u, q]) => qtyWithUnit(q, u || undefined))
    .join(' + ');

  // The closing row: "Total" on the left, the total quantity under Quantity,
  // and the grand total under Amount. Built cell by cell for the same reason
  // the rows above are — the column rules have to reach the bottom of the box.
  const qtyIdx = cols.findIndex((c) => c.id === 'quantity');
  // "Total" sits immediately left of the quantity it labels, not out at the
  // page edge — the eye reads the word and the figure as one thing. The grand
  // total goes in the last column, the bottom-right corner of the box.
  const totalLabelIdx = Math.max(0, qtyIdx - 1);
  const footerCells = Array.from({ length: colCount }, (_, i) => {
    if (i === qtyIdx) return `<td class="r">${escapeHtml(totalQtyLabel)}</td>`;
    if (i === colCount - 1) return `<td class="r">&#8377; ${num(data.total)}</td>`;
    if (i === totalLabelIdx) return '<td class="r">Total</td>';
    return '<td></td>';
  }).join('');

  // ── HSN / rate-wise tax summary ───────────────────────────────────────────
  const summaryTotals = summary.reduce(
    (acc, r) => ({
      taxable: acc.taxable + r.taxable,
      cgst: acc.cgst + r.cgst,
      sgst: acc.sgst + r.sgst,
      igst: acc.igst + r.igst,
      cess: acc.cess + r.cess,
    }),
    { taxable: 0, cgst: 0, sgst: 0, igst: 0, cess: 0 },
  );
  const totalTax =
    summaryTotals.cgst + summaryTotals.sgst + summaryTotals.igst + summaryTotals.cess;

  // Intra-state splits CGST + SGST; inter-state is a single IGST pair.
  // Printing both would leave an empty pair of columns on every bill.
  const summaryRows = summary
    .map((r) => {
      const half = r.gstRate / 2;
      const pair = interState
        ? `<td class="c">${r.gstRate}%</td><td class="r">${num(r.igst)}</td>`
        : `<td class="c">${half}%</td><td class="r">${num(r.cgst)}</td>` +
          `<td class="c">${half}%</td><td class="r">${num(r.sgst)}</td>`;
      const cess = hasCess
        ? `<td class="c">${r.cessRate ? r.cessRate + '%' : '-'}</td><td class="r">${num(r.cess)}</td>`
        : '';
      return `
      <tr>
        <td>${escapeHtml(r.hsn || '-')}</td>
        <td class="r">${num(r.taxable)}</td>
        ${pair}
        ${cess}
        <td class="r">${num(r.cgst + r.sgst + r.igst + r.cess)}</td>
      </tr>`;
    })
    .join('');

  const summaryHead = interState
    ? '<th colspan="2">IGST</th>'
    : '<th colspan="2">CGST</th><th colspan="2">SGST/UTGST</th>';

  const summaryFootPair = interState
    ? `<td class="c"></td><td class="r b">${num(summaryTotals.igst)}</td>`
    : `<td class="c"></td><td class="r b">${num(summaryTotals.cgst)}</td>` +
      `<td class="c"></td><td class="r b">${num(summaryTotals.sgst)}</td>`;

  const taxSummaryBlock = summary.length
    ? `
  <table class="frame hsn">
    <thead>
      <tr>
        <th rowspan="2">HSN/SAC</th>
        <th rowspan="2" class="r">Taxable<br/>Value</th>
        ${summaryHead}
        ${hasCess ? '<th colspan="2">Cess</th>' : ''}
        <th rowspan="2" class="r">Total<br/>Tax Amount</th>
      </tr>
      <tr>
        ${
          interState
            ? '<th class="c">Rate</th><th class="r">Amount</th>'
            : '<th class="c">Rate</th><th class="r">Amount</th><th class="c">Rate</th><th class="r">Amount</th>'
        }
        ${hasCess ? '<th class="c">Rate</th><th class="r">Amount</th>' : ''}
      </tr>
    </thead>
    <tbody>${summaryRows}</tbody>
    <tfoot>
      <tr>
        <td class="r b">Total</td>
        <td class="r b">${num(summaryTotals.taxable)}</td>
        ${summaryFootPair}
        ${hasCess ? `<td class="c"></td><td class="r b">${num(summaryTotals.cess)}</td>` : ''}
        <td class="r b">${num(totalTax)}</td>
      </tr>
    </tfoot>
  </table>
  <div class="words">Tax Amount (in words) : <strong>${escapeHtml(
    amountInWordsINR(totalTax),
  )}</strong></div>`
    : '';

  // ── Bank block ────────────────────────────────────────────────────────────
  const bank = data.bank;
  const hasBank = !!(bank && (bank.name || bank.accountNo || bank.ifsc || bank.branch));
  const branchAndIfs = [bank?.branch, bank?.ifsc].filter(Boolean).join(' & ');
  const bankBlock = hasBank
    ? `
      <div class="bank">
        <div class="bank-title">Company&#39;s Bank Details</div>
        ${bank?.name ? `<div><span class="bk">Bank Name</span>: ${escapeHtml(bank.name)}</div>` : ''}
        ${bank?.accountNo ? `<div><span class="bk">A/c No.</span>: ${escapeHtml(bank.accountNo)}</div>` : ''}
        ${branchAndIfs ? `<div><span class="bk">Branch &amp; IFS Code</span>: ${escapeHtml(branchAndIfs)}</div>` : ''}
      </div>`
    : '';

  // Terms may open with dispatch details (Quick Bill); those fill the ruled
  // reference boxes, and only what follows is the declaration.
  const { details: ref, freeTerms } = parseTerms(data.terms);
  const cell = (k: keyof DispatchDetails) => {
    const v = (ref[k] ?? '').trim();
    if (!v) return '&nbsp;';
    const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
    return escapeHtml(iso
      ? new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3])).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
      : v);
  };
  const extraRefRows = [
    ref.vehicleNo ? `<tr><td colspan="2"><div class="meta-k">Motor Vehicle No.</div><div class="meta-v">${cell('vehicleNo')}</div></td></tr>` : '',
    ref.termsOfDelivery ? `<tr><td colspan="2"><div class="meta-k">Terms of Delivery</div><div class="meta-v">${cell('termsOfDelivery')}</div></td></tr>` : '',
  ].join('');
  const shipToBlock = ref.shipTo
    ? `<div class="party-label" style="margin-top:6px">Consignee (Ship to)</div><div>${cell('shipTo')}</div>`
    : '';

  const declaration = freeTerms.trim()
    ? escapeHtml(freeTerms.trim())
    : 'We declare that this invoice shows the actual price of the goods described and that all particulars are true and correct.';

  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8"/>
<title>${escapeHtml(data.invoiceNumber)}</title>
<style>
  /* Paper margin. The ruled frame is the outermost thing on the page, so
     without this it butts against the sheet edge and the first millimetres get
     clipped by most printers' unprintable area. */
  @page { size: A4; margin: 12mm; }
  * { box-sizing: border-box; }
  body {
    font-family: "Segoe UI", Arial, Helvetica, sans-serif;
    font-size: 10px; color: #000; margin: 0;
    -webkit-print-color-adjust: exact; print-color-adjust: exact;
  }
  /* On screen there is no @page, so the same gap has to come from padding —
     otherwise a preview shows the frame flush to the edge and misrepresents
     what comes out of the printer. Screen only, or it would be added ON TOP of
     the page margin when printing and push the table onto a second sheet. */
  @media screen {
    body { padding: 12mm; }
  }
  table { border-collapse: collapse; width: 100%; }
  .frame, .frame td, .frame th { border: 0.6px solid #000; }
  td, th { padding: 2px 4px; vertical-align: top; }
  th { font-weight: 600; text-align: center; }
  .c { text-align: center; }
  .r { text-align: right; }
  .b { font-weight: 700; }
  .italic { font-style: italic; }

  .stamp { display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 2px; }
  .stamp .title { font-size: 13px; font-weight: 700; }
  .stamp .meta { text-align: right; font-size: 9px; font-style: italic; }
  .stamp .copy { font-weight: 600; font-style: normal; }

  .party-name { font-size: 12px; font-weight: 700; }
  .party-label { font-size: 9px; }
  .seller, .buyer { line-height: 1.35; }

  .meta-cell { padding: 0; vertical-align: top; }

  /* The reference grid. Most of it is empty on a counter sale, and that is
     deliberate: the boxes are what the form looks like, and they leave room to
     write dispatch details by hand on the printed copy. Each cell is ruled, so
     the block reads as a form rather than a list of blank labels. */
  .refgrid { height: 100%; border-collapse: collapse; }
  .refgrid td {
    border-bottom: 0.6px solid #000;
    border-left: 0.6px solid #000;
    padding: 1px 4px;
    vertical-align: top;
  }
  .refgrid tr td:first-child { border-left: none; }
  .refgrid .refgrid-tail td { border-bottom: none; height: 100%; }
  .meta-k { font-size: 9px; line-height: 1.25; }
  .meta-v { font-weight: 700; line-height: 1.3; }

  /* Seller mark, when one is set and self-contained. */
  .logo { float: left; max-height: 40px; max-width: 64px; margin: 0 6px 2px 0; }
  .hdr .seller, .hdr .buyer { vertical-align: top; }

  /* The ruled columns must run down the page even for a short bill — that is
     what makes a printed form read as a form rather than a stub. */
  /* The ruled columns run to a minimum depth, and a single filler row takes
     up whatever is left — without it the browser stretches EVERY row to fill
     the height, drifting the subtotal and tax lines apart down the page. */
  .items tbody { height: 300px; }
  .items tbody tr.filler td { height: 100%; }
  .items tbody td {
    border-left: 0.6px solid #000; border-right: 0.6px solid #000;
    border-top: none; border-bottom: none;
  }
  /* A rule under every item, so each line is a closed box rather than text
     floating between two long verticals. On a bill being checked against goods
     the finger tracks the rule across to the amount. */
  .items tbody tr.item td { border-bottom: 0.6px solid #000; }
  .items tbody tr.sub td { border-top: 0.6px solid #000; }
  /* The tax label sits in the Rate column and is allowed to spill left over
     the empty Quantity cell — the alternative is a colspan, which is what used
     to break the column rules here. */
  .items tbody td.taxlabel { white-space: nowrap; overflow: visible; }
  .items .desc { font-weight: 600; }
  .items tfoot td { border: 0.6px solid #000; font-weight: 700; }

  .words { border: 0.6px solid #000; border-top: none; padding: 3px 4px; }
  .eoe { text-align: right; font-style: italic; font-size: 9px; padding: 1px 4px; }

  .hsn th { font-size: 9px; }

  .foot { display: flex; border: 0.6px solid #000; border-top: none; }
  .foot .left { width: 50%; padding: 4px; border-right: 0.6px solid #000; }
  .foot .right { width: 50%; padding: 4px; display: flex; flex-direction: column; justify-content: space-between; }
  .bank-title { font-weight: 700; margin-bottom: 2px; }
  .bk { display: inline-block; min-width: 96px; }
  .decl-title { font-weight: 700; }
  .sign { text-align: right; }
  .sign .for { font-weight: 700; }
  .sign .line { margin-top: 34px; font-size: 9px; }
  .cgi { text-align: center; font-size: 9px; margin-top: 4px; }
</style></head>
<body>

  <div class="stamp">
    <div class="title">Tax Invoice</div>
    <div class="meta">
      Printed on ${escapeHtml(printStamp(printedAt))}<br/>
      <span class="copy">(${escapeHtml(INVOICE_COPY_LABELS[copyType])})</span>
    </div>
  </div>

  <table class="frame hdr">
    <tr>
      <td style="width:58%" class="seller">
        ${logoBlock}
        <div class="party-name">${escapeHtml(data.storeName)}</div>
        ${sellerLines}
      </td>
      <td class="meta-cell" rowspan="2" style="width:42%">
        <table class="refgrid">
          <tr>
            <td style="width:50%"><div class="meta-k">Invoice No.</div><div class="meta-v">${escapeHtml(
              data.invoiceNumber,
            )}</div></td>
            <td style="width:50%"><div class="meta-k">Dated</div><div class="meta-v">${escapeHtml(
              data.invoiceDate,
            )}</div></td>
          </tr>
          <tr>
            <td colspan="2"><div class="meta-k">Delivery Note</div><div class="meta-v">${cell('deliveryNote')}</div></td>
          </tr>
          <tr>
            <td><div class="meta-k">Reference No. &amp; Date.</div><div class="meta-v">&nbsp;</div></td>
            <td><div class="meta-k">Other References</div><div class="meta-v">&nbsp;</div></td>
          </tr>
          <tr>
            <td><div class="meta-k">Buyer&#39;s Order No.</div><div class="meta-v">${cell('buyerOrderNo')}</div></td>
            <td><div class="meta-k">Dated</div><div class="meta-v">${cell('buyerOrderDate')}</div></td>
          </tr>
          <tr>
            <td><div class="meta-k">Dispatch Doc No.</div><div class="meta-v">${cell('dispatchDocNo')}</div></td>
            <td><div class="meta-k">Delivery Note Date</div><div class="meta-v">${cell('deliveryNoteDate')}</div></td>
          </tr>
          <tr>
            <td><div class="meta-k">Dispatched through</div><div class="meta-v">${cell('dispatchedThrough')}</div></td>
            <td><div class="meta-k">Destination</div><div class="meta-v">${cell('destination')}</div></td>
          </tr>
          ${extraRefRows}
          ${posRow}
          <tr class="refgrid-tail"><td colspan="2"></td></tr>
        </table>
      </td>
    </tr>
    <tr>
      <td class="buyer">
        <div class="party-label">Buyer (Bill to)</div>
        <div class="party-name">${escapeHtml(buyerName)}</div>
        ${buyerLines}
        ${shipToBlock}
      </td>
    </tr>
  </table>

  <table class="frame items">
    <thead>
      <tr>${headerCells}</tr>
    </thead>
    <tbody>
      ${itemRows}
      <!-- The blank space sits BETWEEN the items and the running totals, so the
           subtotal, the taxes and the round off gather just above the Total
           line instead of floating directly under the last item with the empty
           page beneath them. -->
      <tr class="filler">${'<td></td>'.repeat(colCount)}</tr>
      ${totalLineRows.join('')}
    </tbody>
    <tfoot>
      <tr>${footerCells}</tr>
    </tfoot>
  </table>

  <div class="eoe">E. &amp; O.E</div>

  <div class="words">Amount Chargeable (in words)<br/><strong>${escapeHtml(
    amountInWordsINR(data.total),
  )}</strong></div>

  ${taxSummaryBlock}

  <div class="foot">
    <div class="left">
      <div class="decl-title">Declaration</div>
      <div style="white-space:pre-line">${declaration}</div>
    </div>
    <div class="right">
      ${bankBlock}
      <div class="sign">
        <div class="for">for ${escapeHtml(data.storeName)}</div>
        <div class="line">Authorised Signatory</div>
      </div>
    </div>
  </div>

  <div class="cgi">This is a Computer Generated Invoice</div>

</body></html>`;
}
